import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useCockpitDb } from './cockpit/CockpitDataContext';
import { toast } from '../hooks/use-toast';
import { COCKPIT_CSS } from './cockpit/cockpitStyles';
import {
  STAGES, SOURCES, OWNERS, STATUTS, SLSTATUS, CANDST, INTERCO_DAYS, TUTOS,
  stageOf, fmt, daysLeft, initials, missionVal, isHot, matchScore, matchable,
  isInterco, isPlaceable, candidatesFor, bestMatchFor,
  clientOf, entitesOf, entiteObj, sousEntitesOf, equipesOf, equipeObj,
  parseStructure, serializeStructure,
} from './cockpit/cockpitData';

// ─────────────────────────────────────────────────────────────────────────────
// Cockpit BM (bêta) — nouvelle vue de pilotage commercial, portée du prototype
// validé. Coexiste avec le Dashboard historique (/dashboard) et n'affecte pas
// la Bibliothèque DC. Les comptes (grands comptes > entités > équipes) sont
// partagés avec le CRM via CockpitDataContext ; le branchement Supabase suivra
// la roadmap (docs/ROADMAP_COPILOTE_BM.md).
// ─────────────────────────────────────────────────────────────────────────────

const notify = (msg) => toast({ title: msg });
const TUTO_LS = 'skilldeck_cockpit_tuto_v1';

// La navigation primaire vit désormais dans la sidebar (voir AppLayout).
// Le segment d'URL /cockpit/<section> pilote la vue affichée.
const SECTION_TO_VIEW = {
  undefined: 'cockpit',
  matching: 'matching',
  positionnements: 'kanban',
  comptes: 'clients',
  consultants: 'consultants',
  // Sorties de la nav primaire, accessibles depuis le tableau de bord, mais
  // adressables : sans URL propre, revenir au tableau de bord depuis la sidebar
  // ne changerait pas le path et la vue resterait bloquée.
  ao: 'ao',
  shortlists: 'shortlists',
};
const VIEW_LABEL = {
  cockpit: 'Tableau de bord',
  kanban: 'Positionnements',
  list: 'Positionnements',
  clients: 'Comptes',
  account: 'Comptes',
  consultants: 'Consultants',
  matching: 'Matching IA',
  ao: "Appels d'offres",
  shortlists: 'Shortlists',
};

// Surlignage des correspondances de recherche
const Hl = ({ s, q }) => {
  const str = String(s == null ? '' : s);
  if (!q) return str;
  const i = str.toLowerCase().indexOf(q);
  if (i < 0) return str;
  return (
    <>
      {str.slice(0, i)}
      <mark>{str.slice(i, i + q.length)}</mark>
      {str.slice(i + q.length)}
    </>
  );
};

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const ModalShell = ({ onClose, children }) => (
  <div className="ckpt-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="modal">{children}</div>
  </div>
);

const ModalHead = ({ av, title, sub, onClose }) => (
  <div className="modal-head">
    {av ? <div className="mav">{av}</div> : null}
    <div>
      <div className="nm">{title}</div>
      {sub ? <div className="rl">{sub}</div> : null}
    </div>
    <button className="x" onClick={onClose} aria-label="Fermer"><XIcon /></button>
  </div>
);

const Kpi = ({ label, val, sub, cls, subColor }) => (
  <div className="kpi">
    <div className="lbl">{label}</div>
    <div className={`val ${cls || ''}`}>{val}</div>
    <div className="chip" style={subColor ? { color: subColor } : undefined}>{sub}</div>
  </div>
);

// ═════════════════════════ Modales formulaires ═══════════════════════════════

function PosFormModal({ db, id, preset = {}, onClose, commit, onDraft }) {
  const existing = id ? db.pos.find((x) => x.id === id) : null;
  const p = existing || {
    c: preset.c || '', role: preset.role || '', client: preset.client || db.clients[0]?.name || '',
    subclient: preset.subclient || '', entity: preset.entity || '', sub: preset.sub || '', team: preset.team || '',
    ao: preset.ao || '', tjm: preset.tjm || 500, match: 80, stage: 'identifie', owner: OWNERS[0], source: 'Mail',
  };
  const known = db.cons.find((x) => x.name === p.c);
  const [f, setF] = useState({
    consSel: known ? String(known.id) : (p.c ? '__free__' : ''),
    freeName: known ? '' : p.c,
    role: p.role, client: p.client, subclient: p.subclient, entity: p.entity, sub: p.sub, team: p.team,
    ao: p.ao, tjm: p.tjm, match: p.match, source: p.source, owner: p.owner, stage: p.stage,
    ncName: '', ncSector: '', nsclName: '', neName: '', nsName: '', ntName: '',
  });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const sortedCons = db.cons.slice().sort((a, b) => (matchable(b) - matchable(a)) || (isInterco(b) - isInterco(a)) || a.name.localeCompare(b.name));
  const ents = f.client === '__new__' ? [] : entitesOf(db.clients, f.client);
  const ses = f.client === '__new__' || !f.subclient || f.subclient === '__new__' ? [] : sousEntitesOf(db.clients, f.client, f.subclient);
  const eqs = !f.entity || f.entity === '__new__' ? [] : equipesOf(db.clients, f.client, f.subclient, f.entity);
  const doms = !f.sub || f.sub === '__new__' ? [] : ((equipeObj(db.clients, f.client, f.subclient, f.entity, f.sub) || {}).domains || []);

  const pickCons = (v) => {
    const co = db.cons.find((x) => String(x.id) === v);
    setF((o) => ({ ...o, consSel: v, ...(co ? { role: co.role, tjm: co.tjm } : {}) }));
  };
  const submit = (withDraft) => {
    let { client, subclient, entity, sub, team } = f;
    if (client === '__new__') {
      client = f.ncName.trim();
      if (!client) { notify('Indiquez le nom du compte.'); return; }
    }
    if (subclient === '__new__') subclient = f.nsclName.trim();
    if (entity === '__new__') entity = f.neName.trim();
    if (sub === '__new__') sub = f.nsName.trim();
    if (team === '__new__') team = f.ntName.trim();
    const cname = f.consSel === '__free__' ? f.freeName.trim() : (db.cons.find((x) => String(x.id) === f.consSel)?.name || '');
    const data = {
      c: cname || 'Consultant', role: f.role.trim(), client, subclient, entity, sub, team,
      ao: f.ao.trim() || 'AO à préciser', tjm: +f.tjm || 0, match: +f.match || 0,
      source: f.source, owner: f.owner, stage: f.stage,
    };
    commit((d) => {
      if (!d.clients.find((c) => c.name === client)) d.clients.push({ name: client, sector: f.ncSector.trim(), city: '', contact: '', email: '', phone: '', clients: [] });
      const gco = d.clients.find((c) => c.name === client);
      if (subclient) {
        gco.clients = gco.clients || [];
        if (!gco.clients.find((x) => x.name === subclient)) gco.clients.push({ name: subclient, contact: '', email: '', entities: [] });
      }
      const sco = subclient ? gco.clients.find((x) => x.name === subclient) : null;
      if (entity && sco) {
        sco.entities = sco.entities || [];
        if (!sco.entities.find((e) => e.name === entity)) sco.entities.push({ name: entity, subs: [] });
      }
      const eobj = entity && sco ? sco.entities.find((e) => e.name === entity) : null;
      if (sub && eobj) {
        eobj.subs = eobj.subs || [];
        if (!eobj.subs.find((x) => x.name === sub)) eobj.subs.push({ name: sub, domains: [] });
      }
      const sobj = sub && eobj ? eobj.subs.find((x) => x.name === sub) : null;
      if (team && sobj) {
        sobj.domains = sobj.domains || [];
        if (!sobj.domains.includes(team)) sobj.domains.push(team);
      }
      if (id) {
        const t = d.pos.find((x) => x.id === id);
        Object.assign(t, data);
        t.hist.unshift(['Positionnement modifié', 'à l’instant']);
      } else {
        d.pos.unshift({ id: d.nextId++, ...data, days: 0, hist: [['Positionnement créé', 'à l’instant']] });
      }
    });
    notify(id ? 'Positionnement mis à jour.' : 'Positionnement créé.');
    if (withDraft) onDraft({ client, subclient, entity, sub, team, cons: cname, role: f.role.trim(), ao: f.ao.trim(), tjm: f.tjm });
    onClose();
  };
  const cascade = (level, v) => {
    if (level === 'client') setF((o) => ({ ...o, client: v, subclient: '', entity: '', sub: '', team: '' }));
    else if (level === 'subclient') setF((o) => ({ ...o, subclient: v, entity: '', sub: '', team: '' }));
    else if (level === 'entity') setF((o) => ({ ...o, entity: v, sub: '', team: '' }));
    else if (level === 'sub') setF((o) => ({ ...o, sub: v, team: '' }));
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHead title={id ? 'Modifier le positionnement' : 'Nouveau positionnement'} sub={id ? '' : 'Un consultant × un appel d’offres'} onClose={onClose} />
      <div className="modal-body">
        <div className="frow">
          <div className="fl">
            <label>Consultant</label>
            <select value={f.consSel} onChange={(e) => pickCons(e.target.value)}>
              <option value="">— Choisir un consultant —</option>
              {sortedCons.map((co) => (
                <option key={co.id} value={String(co.id)}>{co.name} — {co.role} · {isInterco(co) ? 'à replacer' : co.statut}</option>
              ))}
              <option value="__free__">✍ Autre nom…</option>
            </select>
          </div>
          <div className="fl"><label>Rôle / poste</label><input value={f.role} onChange={(e) => set('role', e.target.value)} placeholder="Lead Dev…" /></div>
        </div>
        {f.consSel === '__free__' && (
          <div className="fl"><label>Nom du consultant</label><input value={f.freeName} onChange={(e) => set('freeName', e.target.value)} placeholder="Prénom Nom" /></div>
        )}
        <div className="frow">
          <div className="fl">
            <label>Compte</label>
            <select value={f.client} onChange={(e) => cascade('client', e.target.value)}>
              {db.clients.map((c) => <option key={c.name}>{c.name}</option>)}
              <option value="__new__">➕ Nouveau compte…</option>
            </select>
          </div>
          <div className="fl">
            <label>Entité</label>
            <select value={f.subclient} onChange={(e) => cascade('subclient', e.target.value)}>
              <option value="">— Aucune —</option>
              {ents.map((x) => <option key={x.name}>{x.name}</option>)}
              <option value="__new__">➕ Nouvelle entité…</option>
            </select>
          </div>
        </div>
        {f.client === '__new__' && (
          <div className="frow">
            <div className="fl"><label>Nom du compte</label><input value={f.ncName} onChange={(e) => set('ncName', e.target.value)} placeholder="Ex. Crédit Agricole" /></div>
            <div className="fl"><label>Secteur</label><input value={f.ncSector} onChange={(e) => set('ncSector', e.target.value)} placeholder="Banque…" /></div>
          </div>
        )}
        {f.subclient === '__new__' && (
          <div className="fl"><label>Nom de la nouvelle entité</label><input value={f.nsclName} onChange={(e) => set('nsclName', e.target.value)} placeholder="Ex. CIB, BP2S, ITG…" /></div>
        )}
        <div className="frow">
          <div className="fl">
            <label>Sous-entité</label>
            <select value={f.entity} onChange={(e) => cascade('entity', e.target.value)}>
              <option value="">— Aucune —</option>
              {ses.map((x) => <option key={x.name}>{x.name}</option>)}
              <option value="__new__">➕ Nouvelle sous-entité…</option>
            </select>
          </div>
          <div className="fl">
            <label>Équipe</label>
            <select value={f.sub} onChange={(e) => cascade('sub', e.target.value)}>
              <option value="">— Aucune —</option>
              {eqs.map((x) => <option key={x.name}>{x.name}</option>)}
              <option value="__new__">➕ Nouvelle équipe…</option>
            </select>
          </div>
        </div>
        {f.entity === '__new__' && (
          <div className="fl"><label>Nom de la nouvelle sous-entité</label><input value={f.neName} onChange={(e) => set('neName', e.target.value)} placeholder="Ex. DATA, Paiements…" /></div>
        )}
        {f.sub === '__new__' && (
          <div className="fl"><label>Nom de la nouvelle équipe</label><input value={f.nsName} onChange={(e) => set('nsName', e.target.value)} placeholder="Ex. Cash Management…" /></div>
        )}
        <div className="fl">
          <label>Domaine (optionnel)</label>
          <select value={f.team} onChange={(e) => set('team', e.target.value)}>
            <option value="">— Aucun —</option>
            {doms.map((t) => <option key={t}>{t}</option>)}
            <option value="__new__">➕ Nouveau domaine…</option>
          </select>
        </div>
        {f.team === '__new__' && (
          <div className="fl"><label>Nom du nouveau domaine</label><input value={f.ntName} onChange={(e) => set('ntName', e.target.value)} placeholder="Ex. Trading, Paiements…" /></div>
        )}
        <div className="fl"><label>Appel d'offres / besoin</label><input value={f.ao} onChange={(e) => set('ao', e.target.value)} placeholder="AO…" /></div>
        <div className="frow">
          <div className="fl"><label>TJM (€/jour)</label><input type="number" value={f.tjm} onChange={(e) => set('tjm', e.target.value)} /></div>
          <div className="fl"><label>Score matching (%)</label><input type="number" value={f.match} onChange={(e) => set('match', e.target.value)} /></div>
        </div>
        <div className="frow">
          <div className="fl"><label>Source / canal</label><select value={f.source} onChange={(e) => set('source', e.target.value)}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div className="fl"><label>Commercial</label><select value={f.owner} onChange={(e) => set('owner', e.target.value)}>{OWNERS.map((o) => <option key={o}>{o}</option>)}</select></div>
        </div>
        <div className="fl"><label>Étape</label><select value={f.stage} onChange={(e) => set('stage', e.target.value)}>{STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        {!id && <button className="btn btn-ghost" style={{ color: 'var(--acc)', borderColor: 'var(--violet)' }} onClick={() => submit(true)}>Créer + brouillon mail</button>}
        <button className="btn btn-primary" onClick={() => submit(false)}>{id ? 'Enregistrer' : 'Créer'}</button>
      </div>
    </ModalShell>
  );
}

function NeedFormModal({ db, id, onClose, onSave }) {
  const n = id ? db.needs.find((x) => x.id === id) : { title: '', client: db.clients[0]?.name || '', subclient: '', skills: [], tjm: 550, deadline: '', source: 'Mail' };
  const [f, setF] = useState({ title: n.title, client: n.client, subclient: n.subclient || '', skills: n.skills.join(', '), tjm: n.tjm, deadline: n.deadline || '', source: n.source || 'Mail' });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const subs = entitesOf(db.clients, f.client);
  const submit = () => {
    const title = f.title.trim();
    if (!title) { notify("L'intitulé est requis."); return; }
    onSave(id, {
      title, client: f.client, subclient: f.subclient,
      skills: f.skills.split(',').map((s) => s.trim()).filter(Boolean),
      tjm: +f.tjm || 0, deadline: f.deadline, source: f.source,
    });
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHead title={id ? "Modifier l'AO" : "Nouvel appel d'offres"} sub={id ? '' : 'Le besoin client à staffer'} onClose={onClose} />
      <div className="modal-body">
        <div className="fl"><label>Intitulé du besoin</label><input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="Ex. Lead Dev React" /></div>
        <div className="frow">
          <div className="fl"><label>Grand compte</label><select value={f.client} onChange={(e) => setF((o) => ({ ...o, client: e.target.value, subclient: '' }))}>{db.clients.map((c) => <option key={c.name}>{c.name}</option>)}</select></div>
          <div className="fl"><label>Entité</label><select value={f.subclient} onChange={(e) => set('subclient', e.target.value)}><option value="">— Aucune —</option>{subs.map((x) => <option key={x.name}>{x.name}</option>)}</select></div>
        </div>
        <div className="fl"><label>Compétences requises (virgules)</label><input value={f.skills} onChange={(e) => set('skills', e.target.value)} placeholder="React, Node.js, AWS" /></div>
        <div className="frow">
          <div className="fl"><label>TJM cible (€/jour)</label><input type="number" value={f.tjm} onChange={(e) => set('tjm', e.target.value)} /></div>
          <div className="fl"><label>Échéance</label><input type="date" value={f.deadline} onChange={(e) => set('deadline', e.target.value)} /></div>
        </div>
        <div className="fl"><label>Source / canal</label><select value={f.source} onChange={(e) => set('source', e.target.value)}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select></div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={submit}>{id ? 'Enregistrer' : 'Créer'}</button>
      </div>
    </ModalShell>
  );
}

function SlFormModal({ db, onClose, onCreate }) {
  const [needId, setNeedId] = useState(db.needs[0] ? String(db.needs[0].id) : '');
  const [owner, setOwner] = useState(OWNERS[0]);
  return (
    <ModalShell onClose={onClose}>
      <ModalHead title="Nouvelle shortlist" sub="Une sélection de candidats pour un besoin" onClose={onClose} />
      <div className="modal-body">
        <div className="fl"><label>Basée sur le besoin / AO</label><select value={needId} onChange={(e) => setNeedId(e.target.value)}>{db.needs.map((n) => <option key={n.id} value={String(n.id)}>{n.title} · {n.client}</option>)}</select></div>
        <div className="fl"><label>Commercial</label><select value={owner} onChange={(e) => setOwner(e.target.value)}>{OWNERS.map((o) => <option key={o}>{o}</option>)}</select></div>
        <div className="ms">Les meilleurs consultants disponibles seront proposés automatiquement (matching). Vous affinez ensuite.</div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={() => { onCreate(+needId, owner); onClose(); }}>Créer</button>
      </div>
    </ModalShell>
  );
}

function ClientFormModal({ db, name, onClose, onSave, onDelete }) {
  const cl = name ? clientOf(db.clients, name) : { name: '', sector: '', city: '', contact: '', email: '', phone: '' };
  const [f, setF] = useState({ name: cl.name, sector: cl.sector || '', city: cl.city || '', contact: cl.contact || '', email: cl.email || '', phone: cl.phone || '' });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const submit = () => {
    const nm = f.name.trim();
    if (!nm) { notify('Le nom du compte est requis.'); return; }
    if (!name && db.clients.find((c) => c.name === nm)) { notify('Ce compte existe déjà.'); return; }
    onSave(name, { name: nm, sector: f.sector.trim(), city: f.city.trim(), contact: f.contact.trim(), email: f.email.trim(), phone: f.phone.trim() });
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHead title={name ? 'Modifier le compte' : 'Nouveau compte'} onClose={onClose} />
      <div className="modal-body">
        <div className="fl"><label>Nom du compte</label><input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex. Crédit Agricole" /></div>
        <div className="frow">
          <div className="fl"><label>Secteur</label><input value={f.sector} onChange={(e) => set('sector', e.target.value)} /></div>
          <div className="fl"><label>Ville</label><input value={f.city} onChange={(e) => set('city', e.target.value)} /></div>
        </div>
        <div className="fl"><label>Contact clé</label><input value={f.contact} onChange={(e) => set('contact', e.target.value)} /></div>
        <div className="frow">
          <div className="fl"><label>Email</label><input value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
          <div className="fl"><label>Téléphone</label><input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
        </div>
        <div className="ms">Les entités (CIB, BP2S, ITG…) s'ajoutent depuis la fiche du compte.</div>
      </div>
      <div className="modal-foot">
        {name && <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => onDelete(name)}>Supprimer</button>}
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={submit}>{name ? 'Enregistrer' : 'Créer'}</button>
      </div>
    </ModalShell>
  );
}

function SubclientFormModal({ db, gc, sclName, onClose, onSave, onDelete }) {
  const gco = clientOf(db.clients, gc);
  const scl = sclName ? (gco.clients || []).find((x) => x.name === sclName) : { name: '', contact: '', email: '', entities: [] };
  const [f, setF] = useState({ name: scl.name, contact: scl.contact || '', email: scl.email || '', struct: serializeStructure(scl.entities) });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const submit = () => {
    const nm = f.name.trim();
    if (!nm) { notify("Le nom de l'entité est requis."); return; }
    if (!sclName && (gco.clients || []).find((e) => e.name === nm)) { notify('Cette entité existe déjà.'); return; }
    onSave(gc, sclName, { name: nm, contact: f.contact.trim(), email: f.email.trim(), entities: parseStructure(f.struct) });
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHead title={sclName ? "Modifier l'entité" : 'Nouvelle entité'} sub={gc} onClose={onClose} />
      <div className="modal-body">
        <div className="fl"><label>Nom de l'entité</label><input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex. CIB, BP2S, ITG…" /></div>
        <div className="frow">
          <div className="fl"><label>Contact</label><input value={f.contact} onChange={(e) => set('contact', e.target.value)} placeholder="Prénom Nom" /></div>
          <div className="fl"><label>Email</label><input value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
        </div>
        <div className="fl">
          <label>Structure (une ligne par équipe — « Sous-entité &gt; Équipe : domaines [ @ Contact &lt;email&gt; ] »)</label>
          <textarea style={{ minHeight: 130 }} value={f.struct} onChange={(e) => set('struct', e.target.value)}
            placeholder={'DATA > Cash Management : Paiements, Reporting @ Jean Dupont <j.dupont@client.com>\nGlobal Markets > Fixed Income : Trading'} />
        </div>
        <div className="ms">Cartographiez cette entité : Sous-entité &gt; Équipe, avec domaines et contact d'équipe optionnels — le contact sert au brouillon mail.</div>
      </div>
      <div className="modal-foot">
        {sclName && <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => onDelete(gc, sclName)}>Supprimer</button>}
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={submit}>{sclName ? 'Enregistrer' : 'Créer'}</button>
      </div>
    </ModalShell>
  );
}

function ConsFormModal({ db, id, onClose, onSave }) {
  const co = id ? db.cons.find((x) => x.id === id) : { name: '', role: '', skills: [], tjm: 500, statut: 'Disponible', client: '', end: '' };
  const [f, setF] = useState({ name: co.name, role: co.role, skills: co.skills.join(', '), tjm: co.tjm, statut: co.statut, client: co.client || '', end: co.end || '' });
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const onMission = f.statut === 'En mission' || f.statut === 'Bientôt dispo';
  const submit = () => {
    const nm = f.name.trim();
    if (!nm) { notify('Le nom est requis.'); return; }
    onSave(id, {
      name: nm, role: f.role.trim(),
      skills: f.skills.split(',').map((s) => s.trim()).filter(Boolean),
      tjm: +f.tjm || 0, statut: f.statut,
      client: onMission ? f.client.trim() : '', end: onMission ? f.end : '',
    });
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHead title={id ? 'Modifier le consultant' : 'Nouveau consultant'} onClose={onClose} />
      <div className="modal-body">
        <div className="frow">
          <div className="fl"><label>Nom</label><input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Prénom Nom" /></div>
          <div className="fl"><label>Rôle / poste</label><input value={f.role} onChange={(e) => set('role', e.target.value)} placeholder="Lead Dev…" /></div>
        </div>
        <div className="fl"><label>Compétences (séparées par des virgules)</label><input value={f.skills} onChange={(e) => set('skills', e.target.value)} placeholder="React, Node.js, AWS" /></div>
        <div className="frow">
          <div className="fl"><label>TJM (€/jour)</label><input type="number" value={f.tjm} onChange={(e) => set('tjm', e.target.value)} /></div>
          <div className="fl"><label>Statut</label><select value={f.statut} onChange={(e) => set('statut', e.target.value)}>{Object.keys(STATUTS).map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>
        {onMission && (
          <div className="frow">
            <div className="fl"><label>Mission actuelle (client)</label><input value={f.client} onChange={(e) => set('client', e.target.value)} placeholder="Client" /></div>
            <div className="fl"><label>Fin de mission</label><input type="date" value={f.end} onChange={(e) => set('end', e.target.value)} /></div>
          </div>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={submit}>{id ? 'Enregistrer' : 'Créer'}</button>
      </div>
    </ModalShell>
  );
}

// ═════════════════════════════ Page Cockpit ══════════════════════════════════

export default function Cockpit() {
  // Données partagées avec le CRM (mêmes comptes, même persistance).
  const { db, update } = useCockpitDb();

  const { section, account: accountParam } = useParams();
  const navigate = useNavigate();
  const urlAccount = accountParam ? decodeURIComponent(accountParam) : null;

  const [view, setView] = useState(() => (urlAccount ? 'account' : (SECTION_TO_VIEW[section] || 'cockpit')));
  const [account, setAccount] = useState(urlAccount);
  const [accOpen, setAccOpen] = useState(() => new Set());
  const [accQ, setAccQ] = useState('');
  const [globalQ, setGlobalQ] = useState('');
  const [openRes, setOpenRes] = useState(() => new Set());
  const [filters, setFilters] = useState({ owner: '', client: '', source: '', statut: '', skill: '', focus: '' });
  const [modal, setModal] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [tutoSeen, setTutoSeen] = useState(() => { try { return JSON.parse(localStorage.getItem(TUTO_LS)) || []; } catch (e) { return []; } });
  const [tutoForce, setTutoForce] = useState(false);
  const dragId = useRef(null);
  const accSearchRef = useRef(null);

  // Vue primaire pilotée par l'URL (la sidebar navigue vers /cockpit/<section>).
  // Les sous-vues locales (liste, AO, shortlists) restent gérées en state.
  useEffect(() => {
    if (urlAccount) { setView('account'); setAccount(urlAccount); }
    else { setView(SECTION_TO_VIEW[section] || 'cockpit'); setAccount(null); }
    setAccQ(''); setOpenRes(new Set());
  }, [section, accountParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Déplie l'organigramme une fois par compte ouvert (sans écraser l'état au fil des mutations).
  const seededAccount = useRef(null);
  useEffect(() => {
    if (!account) { seededAccount.current = null; return; }
    if (!db || seededAccount.current === account) return;
    seededAccount.current = account;
    const cl = clientOf(db.clients, account);
    const open = new Set();
    let eqCount = 0;
    (cl.clients || []).forEach((scl) => (scl.entities || []).forEach((e) => { eqCount += (e.subs || []).length; }));
    (cl.clients || []).forEach((scl) => {
      open.add(`${account}¦${scl.name}`);
      if (eqCount <= 40) (scl.entities || []).forEach((e) => open.add(`${account}¦${scl.name}¦${e.name}`));
    });
    setAccOpen(open);
    window.scrollTo(0, 0);
  }, [account, db]);

  useEffect(() => { setTutoForce(false); }, [view]);

  // Raccourcis clavier : Échap (fermer / effacer), « / » (recherche compte)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (modal) setModal(null);
        else if (view === 'account' && accQ) setAccQ('');
        return;
      }
      if (e.key === '/' && view === 'account' && !modal) {
        const t = document.activeElement;
        if (t && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
        if (accSearchRef.current) { e.preventDefault(); accSearchRef.current.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal, view, accQ]);

  if (!db) {
    return (
      <AppLayout title="Cockpit">
        <style>{COCKPIT_CSS}</style>
        <div className="ckpt" style={{ padding: 24, color: '#9d9bc0' }}>Chargement…</div>
      </AppLayout>
    );
  }

  const { clients, pos, cons, needs, shortlists } = db;

  // ── Actions métier ──────────────────────────────────────────────────────────
  const moveStage = (id, st) => update((d) => {
    const p = d.pos.find((x) => x.id === id);
    if (p && p.stage !== st) { p.stage = st; p.days = 0; p.hist.unshift(['Déplacé vers ' + stageOf(st).label, 'à l’instant']); }
  });
  const relancer = (id) => {
    update((d) => { const p = d.pos.find((x) => x.id === id); if (p) { p.days = 0; p.hist.unshift(['Relance envoyée au client', 'à l’instant']); } });
    notify('Relance enregistrée.');
  };
  const delPos = (id) => { update((d) => { d.pos = d.pos.filter((p) => p.id !== id); }); setModal(null); };
  const saveNeed = (id, data) => update((d) => {
    if (id) Object.assign(d.needs.find((x) => x.id === id), data);
    else d.needs.unshift({ id: d.nextNid++, ...data });
  });
  const delNeed = (id) => { update((d) => { d.needs = d.needs.filter((n) => n.id !== id); }); setModal(null); };
  const createShortlist = (needId, owner) => {
    const n = needs.find((x) => x.id === needId);
    if (!n) { notify('Choisissez un besoin.'); return; }
    update((d) => {
      const top = d.cons.filter(matchable).map((co) => ({ co, sc: matchScore(co, n) })).filter((x) => x.sc >= 40).sort((a, b) => b.sc - a.sc).slice(0, 4);
      d.shortlists.unshift({
        id: d.nextSid++, title: n.title, client: n.client, subclient: n.subclient, ao: n.title,
        skills: n.skills.slice(), owner, status: 'Brouillon', created: 'à l’instant',
        cands: top.map((t) => ({ id: t.co.id, state: 'proposé' })),
      });
    });
    navigate('/cockpit/shortlists');
  };
  const cycleCand = (slId, cid) => update((d) => {
    const c = d.shortlists.find((x) => x.id === slId).cands.find((x) => x.id === cid);
    c.state = c.state === 'proposé' ? 'retenu' : c.state === 'retenu' ? 'écarté' : 'proposé';
  });
  const rmCand = (slId, cid) => update((d) => { const sl = d.shortlists.find((x) => x.id === slId); sl.cands = sl.cands.filter((c) => c.id !== cid); });
  const addCand = (slId, cid) => update((d) => { const sl = d.shortlists.find((x) => x.id === slId); if (!sl.cands.find((c) => c.id === cid)) sl.cands.push({ id: cid, state: 'proposé' }); });
  const sendShortlist = (slId) => {
    let created = 0;
    update((d) => {
      const sl = d.shortlists.find((x) => x.id === slId);
      const ret = sl.cands.filter((c) => c.state === 'retenu');
      (ret.length ? ret : sl.cands).forEach((c) => {
        const co = d.cons.find((x) => x.id === c.id);
        if (!co) return;
        if (!d.pos.find((p) => p.c === co.name && p.ao === sl.ao && p.client === sl.client)) {
          d.pos.unshift({
            id: d.nextId++, c: co.name, role: co.role, client: sl.client, subclient: sl.subclient, ao: sl.ao,
            tjm: co.tjm, match: matchScore(co, { skills: sl.skills }), stage: 'envoye', days: 0, owner: sl.owner,
            source: 'Shortlist', hist: [['Issu de la shortlist ' + sl.title, 'à l’instant'], ['DC envoyé au client', 'à l’instant']],
          });
          created++;
        }
      });
      sl.status = 'Envoyée';
    });
    notify(created + ' positionnement(s) créé(s) en « DC envoyé ».');
  };
  const delSl = (id) => { update((d) => { d.shortlists = d.shortlists.filter((s) => s.id !== id); }); setModal(null); };
  const saveClient = (old, data) => {
    update((d) => {
      if (old) {
        const c = d.clients.find((x) => x.name === old);
        Object.assign(c, data);
        if (old !== data.name) d.pos.filter((p) => p.client === old).forEach((p) => { p.client = data.name; });
      } else d.clients.push({ ...data, clients: [] });
    });
    // Le nom du compte est dans l'URL : après renommage, on remplace l'entrée
    // d'historique pour ne pas laisser un path pointant vers l'ancien nom.
    if (view === 'account' && old && old !== data.name) {
      navigate('/cockpit/comptes/' + encodeURIComponent(data.name), { replace: true });
    }
  };
  const delClient = (name) => {
    if (pos.some((p) => p.client === name)) { notify('Impossible : ce grand compte a des positionnements.'); return; }
    update((d) => { d.clients = d.clients.filter((c) => c.name !== name); });
    setModal(null); navigate('/cockpit/comptes');
  };
  const saveSubclient = (gc, old, data) => update((d) => {
    const gco = d.clients.find((x) => x.name === gc);
    gco.clients = gco.clients || [];
    if (old) {
      const e = gco.clients.find((x) => x.name === old);
      Object.assign(e, data);
      if (old !== data.name) d.pos.filter((p) => p.client === gc && p.subclient === old).forEach((p) => { p.subclient = data.name; });
    } else gco.clients.push(data);
  });
  const delSubclient = (gc, name) => {
    if (pos.some((p) => p.client === gc && p.subclient === name)) { notify('Impossible : des positionnements sont rattachés à cette entité.'); return; }
    update((d) => { const gco = d.clients.find((x) => x.name === gc); gco.clients = (gco.clients || []).filter((e) => e.name !== name); });
    setModal(null);
  };
  const saveCons = (id, data) => update((d) => {
    if (id) { const co = d.cons.find((x) => x.id === id); Object.assign(co, data); co.hist.unshift(['Fiche mise à jour', 'à l’instant']); }
    else d.cons.unshift({ id: d.nextCid++, ...data, hist: [['Consultant ajouté au vivier', 'à l’instant']] });
  });
  const delCons = (id) => { update((d) => { d.cons = d.cons.filter((c) => c.id !== id); }); setModal(null); };

  const draftToClient = (o) => {
    const scl = entiteObj(clients, o.client, o.subclient);
    const sbT = equipeObj(clients, o.client, o.subclient, o.entity, o.sub);
    const gc = clientOf(clients, o.client);
    const email = (sbT && sbT.email) || (scl && scl.email) || gc.email || '';
    const contact = (sbT && sbT.contact) || (scl && scl.contact) || gc.contact || '';
    const loc = [o.subclient, o.entity, o.sub, o.team].filter(Boolean).join(' › ') || o.client;
    const subject = `Profil ${o.cons || ''} — ${o.role || 'consultant'} pour ${o.subclient || o.client}`.replace(/\s+/g, ' ').trim();
    const body = `Bonjour ${contact ? contact.split(' ')[0] : ''},\n\nJe vous propose ${o.cons || 'un profil'}${o.role ? ' (' + o.role + ')' : ''} pour ${loc}${o.ao && o.ao !== 'AO à préciser' ? ' — ' + o.ao : ''}${o.tjm ? ' (TJM ' + o.tjm + '€/jour)' : ''}.\n\nLe dossier de compétences SkillDeck est en pièce jointe. Je reste disponible pour en échanger.\n\nBien à vous,`;
    if (!email) notify("Pas d'email de contact — renseignez la structure de l'entité pour le pré-remplir.");
    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const posFromTeam = (gc, en, se, eq, dom) => setModal({ type: 'posForm', preset: { client: gc, subclient: en, entity: se, sub: eq, team: dom } });
  const posConsToTeam = (coId, gc, en, se, eq) => {
    const co = cons.find((c) => c.id === coId);
    if (!co) return;
    setModal({ type: 'posForm', preset: { c: co.name, role: co.role, tjm: co.tjm, client: gc, subclient: en, entity: se, sub: eq } });
  };
  const posFromMatch = (coId, nId) => {
    const co = cons.find((c) => c.id === coId);
    const n = needs.find((x) => x.id === nId);
    setModal({ type: 'posForm', preset: { c: co.name, role: co.role, tjm: n.tjm, client: n.client, subclient: n.subclient, ao: n.title } });
  };
  const posFromCons = (coId) => {
    const co = cons.find((x) => x.id === coId);
    setModal({ type: 'posForm', preset: { c: co.name, role: co.role, tjm: co.tjm } });
  };

  const accKey = (...args) => args.join('¦');
  const openAccount = (name) => { setModal(null); navigate('/cockpit/comptes/' + encodeURIComponent(name)); };
  const accToggle = (path) => setAccOpen((prev) => { const n = new Set(prev); if (n.has(path)) n.delete(path); else n.add(path); return n; });
  const accAllPaths = () => {
    const cl = clientOf(clients, account); const out = [];
    (cl.clients || []).forEach((scl) => {
      const pc = accKey(account, scl.name); out.push(pc);
      (scl.entities || []).forEach((e) => { const pe = accKey(pc, e.name); out.push(pe); (e.subs || []).forEach((sb) => out.push(accKey(pe, sb.name))); });
    });
    return out;
  };

  // ── Sélections dérivées ─────────────────────────────────────────────────────
  const filtered = pos.filter((p) => (!filters.owner || p.owner === filters.owner) && (!filters.client || p.client === filters.client) && (!filters.source || p.source === filters.source));
  const filteredCons = cons.filter((co) => (!filters.statut || co.statut === filters.statut) && (!filters.skill || co.skills.includes(filters.skill)));

  const accHas = (q, ...vals) => vals.some((v) => (v || '').toLowerCase().includes(q));
  const accTeamRows = (q) => {
    const cl = clientOf(clients, account);
    const items = pos.filter((p) => p.client === account);
    const rows = [];
    (cl.clients || []).forEach((scl) => (scl.entities || []).forEach((e) => (e.subs || []).forEach((sb) => {
      const doms = sb.domains || [];
      const hay = [scl.name, e.name, sb.name, scl.contact || '', sb.contact || '', sb.email || '', doms.join(' ')].join(' ').toLowerCase();
      if (q && !hay.includes(q)) return;
      rows.push({ scl, e, sb, doms, pos: items.filter((p) => p.subclient === scl.name && p.entity === e.name && p.sub === sb.name) });
    })));
    return rows;
  };
  const accFitCons = (row) => {
    const words = [row.sb.name, row.e.name].concat(row.doms).join(' ').toLowerCase().split(/[^a-zà-ÿ0-9+#]+/).filter((w) => w.length > 2);
    const terms = words.join(' ');
    const hist = pos.filter((p) => p.client === account && p.subclient === row.scl.name && (!p.entity || p.entity === row.e.name));
    return cons.filter(matchable).map((co) => {
      const cw = (co.role + ' ' + co.skills.join(' ')).toLowerCase();
      let sc = words.filter((w) => cw.includes(w)).length;
      sc += co.skills.filter((s) => terms.includes(s.toLowerCase())).length;
      const past = hist.some((p) => p.c === co.name);
      if (past) sc += 2;
      if (isInterco(co)) sc += 0.5;
      return { co, sc, past };
    }).sort((a, b) => b.sc - a.sc || a.co.name.localeCompare(b.co.name)).slice(0, 3);
  };

  // ── Tutoriel contextuel ─────────────────────────────────────────────────────
  const tKey = view === 'list' ? 'kanban' : view;
  const tuto = TUTOS[tKey];
  const tutoVisible = tuto && (tutoForce || !tutoSeen.includes(tKey));
  const closeTuto = () => {
    setTutoForce(false);
    setTutoSeen((prev) => {
      const n = prev.includes(tKey) ? prev : [...prev, tKey];
      try { localStorage.setItem(TUTO_LS, JSON.stringify(n)); } catch (e) { /* plein */ }
      return n;
    });
  };

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const renderKpis = () => {
    if (view === 'account') return null;
    if (view === 'shortlists') {
      const draft = shortlists.filter((s) => s.status === 'Brouillon').length;
      const sent = shortlists.filter((s) => s.status === 'Envoyée').length;
      const cands = shortlists.reduce((a, s) => a + s.cands.length, 0);
      return (<div className="kpis">
        <Kpi label="Shortlists actives" val={shortlists.length} sub="en cours" />
        <Kpi label="En brouillon" val={draft} sub="à finaliser" cls="alert" subColor="var(--a)" />
        <Kpi label="Envoyées" val={sent} sub="en attente retour" cls="good" />
        <Kpi label="Candidats proposés" val={cands} sub="au total" />
      </div>);
    }
    if (view === 'ao') {
      const sansC = needs.filter((n) => candidatesFor(pos, n).length === 0).length;
      const soon = needs.filter((n) => { const dl = daysLeft(n.deadline); return dl != null && dl <= 7; }).length;
      const staffable = needs.filter((n) => bestMatchFor(cons, n) >= 70).length;
      return (<div className="kpis">
        <Kpi label="AOs ouverts" val={needs.length} sub="à staffer" />
        <Kpi label="Sans candidat" val={sansC} sub="à traiter" cls="alert" subColor="var(--a)" />
        <Kpi label="Échéance < 7 j" val={soon} sub="urgents" cls="alert" subColor="var(--lose)" />
        <Kpi label="Match dispo ≥ 70%" val={staffable} sub="staffables" cls="good" />
      </div>);
    }
    if (view === 'matching') {
      const avail = cons.filter(matchable);
      let strong = 0;
      needs.forEach((n) => avail.forEach((co) => { if (matchScore(co, n) >= 70) strong++; }));
      return (<div className="kpis">
        <Kpi label="Besoins ouverts" val={needs.length} sub="à staffer" />
        <Kpi label="Consultants dispo" val={avail.length} sub="matchables" cls="good" />
        <Kpi label="Rapprochements ≥ 70%" val={strong} sub="à pousser" cls="good" />
        <Kpi label="Intercontrats" val={cons.filter(isInterco).length} sub="à replacer" cls="alert" subColor="var(--lose)" />
      </div>);
    }
    if (view === 'consultants') {
      const anticip = cons.filter((co) => (co.statut === 'En mission' || co.statut === 'Bientôt dispo') && daysLeft(co.end) != null && daysLeft(co.end) <= INTERCO_DAYS).length;
      return (<div className="kpis">
        <Kpi label="Effectif total" val={cons.length} sub="consultants" />
        <Kpi label="Disponibles maintenant" val={cons.filter(isPlaceable).length} sub="à placer" cls="good" />
        <Kpi label="Fin de mission < 30 j" val={anticip} sub="à anticiper" cls="alert" subColor="var(--a)" />
        <Kpi label="En intercontrat" val={cons.filter((c) => c.statut === 'Intercontrat').length} sub="coût bench" cls="alert" subColor="var(--lose)" />
      </div>);
    }
    const open = filtered.filter((p) => !['gagne', 'perdu'].includes(p.stage));
    const pond = open.reduce((s, p) => s + missionVal(p) * stageOf(p.stage).proba, 0);
    const aos = new Set(open.map((p) => p.ao)).size;
    const won = filtered.filter((p) => p.stage === 'gagne').length;
    const lost = filtered.filter((p) => p.stage === 'perdu').length;
    return (<div className="kpis">
      <Kpi label="Pipeline pondéré" val={fmt(Math.round(pond))} sub="prévisionnel" />
      <Kpi label="AOs actifs" val={aos} sub="en cours" />
      <Kpi label="Taux de transfo" val={((won + lost) ? Math.round((won / (won + lost)) * 100) : 0) + ' %'} sub="gagné / traité" />
      <Kpi label="À relancer" val={filtered.filter(isHot).length} sub="action requise" cls="alert" subColor="var(--lose)" />
    </div>);
  };

  // ── Cartes / lignes réutilisées ─────────────────────────────────────────────
  const posCard = (p) => {
    const s = stageOf(p.stage);
    let rel;
    if (p.stage === 'gagne') rel = <span className="relance won">✓ Signé</span>;
    else if (p.stage === 'perdu') rel = <span className="relance ok">Clôturé</span>;
    else if (p.stage === 'entretien' && p.interview) rel = <span className="relance ok">📅 {p.interview}</span>;
    else if (isHot(p)) rel = <span className="relance hot">⏰ J+{p.days}</span>;
    else rel = <span className="relance ok">J+{p.days}</span>;
    return (
      <div key={p.id} className={`card${isHot(p) ? ' flag' : ''}`} draggable
        onDragStart={() => { dragId.current = p.id; }}
        onClick={() => setModal({ type: 'posDetail', id: p.id })}>
        <div className="nm">{p.c}</div>
        <div className="meta"><span className="av" style={{ background: s.color }} />{p.role} · {p.client}{p.subclient ? ' / ' + p.subclient : ''}{p.sub ? ' › ' + p.sub : ''}</div>
        <div className="row"><span className="tjm">{p.tjm}€/j</span>{!['gagne', 'perdu'].includes(p.stage) && <span className="match">match {p.match}%</span>}</div>
        <div className="row" style={{ marginTop: 6 }}>{rel}<span className="src">{p.source}</span></div>
        <div className="row" style={{ marginTop: 5 }}><span style={{ fontSize: '.66rem', color: 'var(--muted2)', fontWeight: 600 }}>{p.owner}</span></div>
      </div>
    );
  };

  const miniPosRow = (p, right) => (
    <div key={p.id} className="mini-row" onClick={() => setModal({ type: 'posDetail', id: p.id })}>
      <div><div className="ml">{p.c}</div><div className="ms">{p.client} · {p.role}</div></div>
      {right}
    </div>
  );

  // ── Vues ────────────────────────────────────────────────────────────────────
  const isEmptyDb = !clients.length && !cons.length && !pos.length && !needs.length && !shortlists.length;

  const renderOnboarding = () => (
    <div>
      <div style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: 4 }}>Bienvenue dans le Cockpit 👋</div>
      <div style={{ color: 'var(--muted)', fontSize: '.88rem', marginBottom: 20 }}>
        Votre espace de pilotage commercial démarre vierge. Commencez par renseigner vos données réelles :
      </div>
      <div className="ckpt-onboard">
        <div className="ob-card">
          <div className="ob-ic">🏢</div>
          <div className="ob-t">Vos comptes clients</div>
          <div className="ob-d">Ajoutez un grand compte, puis cartographiez ses entités et équipes.</div>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'clientForm', name: null })}>+ Grand compte</button>
        </div>
        <div className="ob-card">
          <div className="ob-ic">👤</div>
          <div className="ob-t">Votre vivier de consultants</div>
          <div className="ob-d">Compétences, TJM, disponibilité — la base du matching.</div>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'consForm' })}>+ Consultant</button>
        </div>
        <div className="ob-card">
          <div className="ob-ic">📋</div>
          <div className="ob-t">Vos besoins / AO</div>
          <div className="ob-d">Saisissez un appel d'offres pour lancer les rapprochements.</div>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'needForm' })}>+ Nouvel AO</button>
        </div>
      </div>
    </div>
  );

  const renderCockpitView = () => {
    if (isEmptyDb) return renderOnboarding();
    const relances = pos.filter(isHot);
    const entretiens = pos.filter((p) => p.stage === 'entretien');
    const negos = pos.filter((p) => p.stage === 'nego');
    const interco = cons.filter(isInterco);
    const aoUrgent = needs.filter((n) => { const dl = daysLeft(n.deadline); return candidatesFor(pos, n).length === 0 || (dl != null && dl <= 7); });
    const total = relances.length + entretiens.length + negos.length + interco.length + aoUrgent.length;
    const hour = new Date().getHours();
    const greet = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';
    const panel = (title, color, rows, empty) => (
      <div className="client-card" style={{ cursor: 'default' }} key={title}>
        <div className="ch" style={{ marginBottom: 12 }}>
          <div className="clogo" style={{ background: color + '22', color }}>{title.slice(0, 1)}</div>
          <div><div className="cn" style={{ fontSize: '.95rem' }}>{title}</div><div className="cmeta">{rows.length} à traiter</div></div>
        </div>
        {rows.length ? rows : <div className="ms" style={{ color: 'var(--muted2)' }}>{empty}</div>}
      </div>
    );
    return (
      <div>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 3 }}>{greet} 👋</div>
        <div style={{ color: 'var(--muted)', fontSize: '.85rem', marginBottom: 16 }}>
          {total ? `${total} action(s) vous attendent aujourd'hui.` : 'Tout est à jour. Rien à traiter pour le moment.'}
        </div>
        <div className="ckpt-quick">
          <button onClick={() => navigate('/cockpit/ao')}>📋 Appels d'offres <b>{needs.length}</b></button>
          <button onClick={() => navigate('/cockpit/shortlists')}>≡ Shortlists <b>{shortlists.length}</b></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 14 }}>
          {panel('Relances dues', '#F87171', relances.map((p) => miniPosRow(p,
            <button className="btn btn-ghost" style={{ padding: '6px 12px' }} onClick={(e) => { e.stopPropagation(); relancer(p.id); }}>Relancer</button>)), 'Aucune relance en retard.')}
          {panel('Entretiens à venir', '#FACC15', entretiens.map((p) => miniPosRow(p,
            <span className="pill" style={{ background: 'rgba(250,204,21,.16)', color: 'var(--a)' }}>{p.interview || 'à planifier'}</span>)), 'Aucun entretien planifié.')}
          {panel('Négociations à closer', '#A099E8', negos.map((p) => miniPosRow(p,
            <span className="pill" style={{ background: '#A099E822', color: '#A099E8' }}>à closer</span>)), 'Aucune négo en cours.')}
          {panel('Intercontrats à anticiper', '#F87171', interco.map((co) => {
            const dl = daysLeft(co.end);
            const lab = co.statut === 'Intercontrat' ? 'intercontrat' : 'fin dans ' + dl + ' j';
            return (
              <div key={co.id} className="mini-row" onClick={() => setModal({ type: 'consDetail', id: co.id })}>
                <div><div className="ml">{co.name}</div><div className="ms">{co.role} · {lab}</div></div>
                <button className="btn btn-ghost" style={{ padding: '6px 12px' }} onClick={(e) => { e.stopPropagation(); posFromCons(co.id); }}>Positionner</button>
              </div>
            );
          }), 'Effectif bien staffé.')}
          {panel('AOs à staffer', '#60A5FA', aoUrgent.map((n) => {
            const dl = daysLeft(n.deadline);
            const cand = candidatesFor(pos, n).length;
            return (
              <div key={n.id} className="mini-row" onClick={() => setModal({ type: 'needDetail', id: n.id })}>
                <div><div className="ml">{n.title}</div><div className="ms">{n.client} · {cand ? cand + ' candidat(s)' : 'à staffer'}{dl != null ? ' · J-' + dl : ''}</div></div>
                <span className="pill" style={{ background: 'var(--violet-soft)', color: 'var(--acc)' }}>best {bestMatchFor(cons, n)}%</span>
              </div>
            );
          }), 'Tous les AO ont un candidat.')}
        </div>
      </div>
    );
  };

  const posFilters = (
    <>
      <select value={filters.owner} onChange={(e) => setFilters((f) => ({ ...f, owner: e.target.value }))}>
        <option value="">Tous les commerciaux</option>
        {OWNERS.map((o) => <option key={o}>{o}</option>)}
      </select>
      <select value={filters.client} onChange={(e) => setFilters((f) => ({ ...f, client: e.target.value }))}>
        <option value="">Tous les comptes</option>
        {clients.map((c) => c.name).sort().map((n) => <option key={n}>{n}</option>)}
      </select>
      <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}>
        <option value="">Toutes les sources</option>
        {[...new Set(pos.map((p) => p.source))].sort().map((s) => <option key={s}>{s}</option>)}
      </select>
    </>
  );

  const renderKanban = () => (
    <div className="board">
      {STAGES.map((s) => {
        const items = filtered.filter((p) => p.stage === s.id);
        const sum = items.filter((p) => !['gagne', 'perdu'].includes(p.stage)).reduce((a, p) => a + missionVal(p) * s.proba, 0);
        return (
          <div className="col" key={s.id}>
            <div className="col-head">
              <span className="dot" style={{ background: s.color }} />{s.label}
              <span className="cnt">{items.length}</span>
              {!['gagne', 'perdu'].includes(s.id) && items.length > 0 && <span className="sum">{fmt(Math.round(sum))} pondéré</span>}
            </div>
            <div className={`col-body${dragOverCol === s.id ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverCol(s.id); }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => { e.preventDefault(); setDragOverCol(null); if (dragId.current) { moveStage(dragId.current, s.id); dragId.current = null; } }}>
              {items.length ? items.map(posCard) : <div style={{ fontSize: '.74rem', color: 'var(--muted2)', textAlign: 'center', padding: '16px 0' }}>—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderList = () => (
    <div className="listview">
      <table>
        <thead><tr><th>Consultant</th><th>Client / AO</th><th>Étape</th><th>TJM</th><th>Match</th><th>Source</th><th>Commercial</th><th>Relance</th></tr></thead>
        <tbody>
          {filtered.length ? filtered.map((p) => {
            const s = stageOf(p.stage);
            return (
              <tr key={p.id} onClick={() => setModal({ type: 'posDetail', id: p.id })}>
                <td style={{ fontWeight: 700 }}>{p.c}<div style={{ fontSize: '.72rem', color: 'var(--muted)', fontWeight: 500 }}>{p.role}</div></td>
                <td>{p.client}<div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{p.ao}</div></td>
                <td><span className="pill" style={{ background: s.color + '22', color: s.color }}>{s.label}</span></td>
                <td style={{ fontWeight: 700 }}>{p.tjm}€/j</td>
                <td>{['gagne', 'perdu'].includes(p.stage) ? '—' : p.match + '%'}</td>
                <td>{p.source}</td><td>{p.owner}</td>
                <td>{isHot(p) ? <span style={{ color: 'var(--lose)', fontWeight: 700 }}>J+{p.days} ⚠</span> : (['gagne', 'perdu'].includes(p.stage) ? '—' : 'J+' + p.days)}</td>
              </tr>
            );
          }) : <tr><td colSpan={8} style={{ color: 'var(--muted2)' }}>Aucun positionnement.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderClients = () => {
    const q = globalQ.trim().toLowerCase();
    if (q) {
      const locs = [];
      clients.forEach((gc) => (gc.clients || []).forEach((scl) => (scl.entities || []).forEach((se) => (se.subs || []).forEach((eq) => {
        const doms = eq.domains || [];
        if (doms.some((d) => d.toLowerCase().includes(q)) || eq.name.toLowerCase().includes(q) || se.name.toLowerCase().includes(q) || scl.name.toLowerCase().includes(q))
          locs.push({ gc: gc.name, ent: scl.name, se: se.name, eq: eq.name, doms: doms.filter((d) => d.toLowerCase().includes(q)), contact: eq.contact || scl.contact });
      }))));
      const matchCons = cons.filter((c) => c.role.toLowerCase().includes(q) || c.skills.some((x) => x.toLowerCase().includes(q)));
      const matchPos = pos.filter((p) => (p.team || '').toLowerCase().includes(q) || (p.sub || '').toLowerCase().includes(q) || (p.subclient || '').toLowerCase().includes(q) || p.role.toLowerCase().includes(q));
      return (
        <div className="cardsgrid">
          <div className="client-card" style={{ cursor: 'default' }}>
            <div className="ch" style={{ marginBottom: 12 }}>
              <div className="clogo">🎯</div>
              <div><div className="cn" style={{ fontSize: '.95rem' }}>Équipes qui font « {q} »</div><div className="cmeta">{locs.length} résultat(s)</div></div>
            </div>
            {locs.length ? locs.map((l, i) => (
              <div key={i} className="mini-row" onClick={() => openAccount(l.gc)}>
                <div><div className="ml">{l.eq}{l.doms.length ? ' · ' + l.doms.join(', ') : ''}</div><div className="ms">{l.gc} › {l.ent} › {l.se}{l.contact ? ' · ' + l.contact : ''}</div></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="iconbtn" onClick={(e) => { e.stopPropagation(); posFromTeam(l.gc, l.ent, l.se, l.eq, ''); }}>+ Profil</button>
                  <span style={{ color: 'var(--muted2)' }}>→</span>
                </div>
              </div>
            )) : <div className="ms">Aucune équipe référencée sur ce périmètre.</div>}
          </div>
          <div className="client-card" style={{ cursor: 'default' }}>
            <div className="ch" style={{ marginBottom: 12 }}>
              <div className="clogo">👤</div>
              <div><div className="cn" style={{ fontSize: '.95rem' }}>Profils correspondants</div><div className="cmeta">{matchCons.length} consultant(s) · {matchPos.length} positionnement(s)</div></div>
            </div>
            {matchCons.map((c) => (
              <div key={'c' + c.id} className="mini-row" onClick={() => setModal({ type: 'consDetail', id: c.id })}>
                <div><div className="ml">{c.name}</div><div className="ms">{c.role} · {c.skills.join(', ')}</div></div>
                <span className="pill" style={{ background: (STATUTS[c.statut] || '#888') + '22', color: STATUTS[c.statut] || '#888' }}>{c.statut}</span>
              </div>
            ))}
            {matchPos.map((p) => (
              <div key={'p' + p.id} className="mini-row" onClick={() => setModal({ type: 'posDetail', id: p.id })}>
                <div><div className="ml">{p.c}</div><div className="ms">{p.role} · {p.client}{p.team ? ' › ' + p.team : ''}</div></div>
                <span className="pill" style={{ background: stageOf(p.stage).color + '22', color: stageOf(p.stage).color }}>{stageOf(p.stage).label}</span>
              </div>
            ))}
            {!matchCons.length && !matchPos.length && <div className="ms">Aucun profil correspondant.</div>}
          </div>
        </div>
      );
    }
    if (!clients.length) {
      return (
        <div className="ckpt-empty">
          <div className="ic">🏢</div>
          <div className="t">Aucun compte pour l'instant</div>
          <div className="d">Ajoutez vos grands comptes clients (Société Générale, BNP…), puis cartographiez leurs entités et équipes.</div>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'clientForm', name: null })}>+ Ajouter un grand compte</button>
        </div>
      );
    }
    return (
      <div className="cardsgrid">
        {clients.slice().sort((a, b) => a.name.localeCompare(b.name)).map((cl) => {
          const items = pos.filter((p) => p.client === cl.name && (!filters.owner || p.owner === filters.owner) && (!filters.source || p.source === filters.source));
          const open = items.filter((p) => !['gagne', 'perdu'].includes(p.stage));
          const won = items.filter((p) => p.stage === 'gagne');
          const lost = items.filter((p) => p.stage === 'perdu').length;
          const tx = (won.length + lost) ? Math.round((won.length / (won.length + lost)) * 100) : 0;
          const ca = won.reduce((a, p) => a + missionVal(p), 0);
          const pond = open.reduce((a, p) => a + missionVal(p) * stageOf(p.stage).proba, 0);
          const hot = items.filter(isHot).length;
          return (
            <div key={cl.name} className="client-card" onClick={() => openAccount(cl.name)}>
              <div className="ch">
                <div className="clogo">{initials(cl.name)}</div>
                <div><div className="cn">{cl.name}</div><div className="cmeta">{cl.sector || '—'}{cl.clients?.length ? ' · ' + cl.clients.length + ' entités' : ''} · {items.length} pos.</div></div>
              </div>
              <div className="cgrid">
                <div className="stat"><div className="sk">Pipeline pondéré</div><div className="sv">{fmt(Math.round(pond))}</div></div>
                <div className="stat"><div className="sk">CA gagné</div><div className="sv win">{fmt(ca)}</div></div>
                <div className="stat"><div className="sk">Taux transfo</div><div className="sv">{tx} %</div></div>
                <div className="stat"><div className="sk">AOs en cours</div><div className="sv">{new Set(open.map((p) => p.ao)).size}</div></div>
              </div>
              {cl.clients?.length > 0 && (
                <div className="scl-chips">
                  {cl.clients.slice(0, 4).map((s) => <span key={s.name} className="scl-chip">{s.name}</span>)}
                  {cl.clients.length > 4 && <span className="scl-chip more">+{cl.clients.length - 4}</span>}
                </div>
              )}
              <div className="cfoot">
                <span>{won.length} placé(s) · {open.length} en cours</span>
                {hot ? <span className="hot">{hot} à relancer</span> : <span>à jour</span>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Fiche compte ────────────────────────────────────────────────────────────
  const renderAccount = () => {
    const name = account;
    const cl = clientOf(clients, name);
    const items = pos.filter((p) => p.client === name);
    const open = items.filter((p) => !['gagne', 'perdu'].includes(p.stage));
    const won = items.filter((p) => p.stage === 'gagne');
    const lost = items.filter((p) => p.stage === 'perdu').length;
    const tx = (won.length + lost) ? Math.round((won.length / (won.length + lost)) * 100) : 0;
    const ca = won.reduce((a, p) => a + missionVal(p), 0);
    const pond = open.reduce((a, p) => a + missionVal(p) * stageOf(p.stage).proba, 0);
    const scls = cl.clients || [];
    const q = accQ.trim().toLowerCase();
    const dash = <span style={{ color: 'var(--muted2)' }}>—</span>;

    // Résultats de recherche (équipes à plat)
    const rows = q ? accTeamRows(q) : [];
    const results = q ? (
      <div className="res-wrap">
        {rows.length ? (
          <>
            <div className="res-head"><b>{rows.length}</b> équipe(s) pour « {q} » — cliquez pour voir les profils dispo, ou <b>Envoyer un profil</b> directement.</div>
            {rows.map((r, i) => {
              const key = accKey(r.scl.name, r.e.name, r.sb.name);
              const isOpen = rows.length === 1 || openRes.has(key);
              const ct = r.sb.contact || r.scl.contact;
              const path = `${r.scl.name} › ${r.e.name}${ct ? ' · ' + ct : ''}${r.pos.length ? ' · ' + r.pos.length + ' pos.' : ''}`;
              const fits = accFitCons(r);
              return (
                <div key={key} className={`res-team${isOpen ? ' open' : ''}`}>
                  <div className="res-team-head" onClick={() => setOpenRes((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; })}>
                    <span className="tn-ico s">◇</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="rt-n"><Hl s={r.sb.name} q={q} /></div>
                      <div className="rt-path"><Hl s={path} q={q} /></div>
                      <div className="rt-doms">
                        {r.doms.length ? r.doms.map((d) => (
                          <button key={d} className="domain-chip" title={`Envoyer un profil sur ${d}`}
                            onClick={(e) => { e.stopPropagation(); posFromTeam(name, r.scl.name, r.e.name, r.sb.name, d); }}>
                            <Hl s={d} q={q} />
                          </button>
                        )) : <span className="ms" style={{ color: 'var(--muted2)' }}>domaines à préciser</span>}
                      </div>
                    </div>
                    <div className="res-actions">
                      {fits.length > 0 && <span className="pill" style={{ background: 'var(--violet-soft)', color: 'var(--acc)' }}>{fits.length} dispo</span>}
                      <button className="btn btn-primary" style={{ padding: '8px 14px' }}
                        onClick={(e) => { e.stopPropagation(); posFromTeam(name, r.scl.name, r.e.name, r.sb.name, ''); }}>
                        Envoyer un profil
                      </button>
                      <span className="res-caret">▸</span>
                    </div>
                  </div>
                  <div className="res-cons">
                    <div className="rc-t">Profils dispo à pousser ici</div>
                    {fits.length ? fits.map(({ co, past }) => (
                      <div key={co.id} className="mini-row" style={{ cursor: 'default' }}>
                        <div>
                          <div className="ml">{co.name}{isInterco(co) && <span style={{ color: 'var(--lose)' }}> · à replacer</span>}{past && <span style={{ color: 'var(--win)' }}> · déjà positionné ici</span>}</div>
                          <div className="ms">{co.role} · {co.skills.join(', ')}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="pill" style={{ background: (STATUTS[co.statut] || '#888') + '22', color: STATUTS[co.statut] || '#888' }}>{co.statut}</span>
                          <button className="btn btn-primary" style={{ padding: '6px 12px' }} onClick={() => posConsToTeam(co.id, name, r.scl.name, r.e.name, r.sb.name)}>Positionner ici</button>
                        </div>
                      </div>
                    )) : <div className="ms" style={{ color: 'var(--muted)' }}>Aucun consultant disponible pour le moment.</div>}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="res-empty">Aucune équipe ne correspond à <b>« {q} »</b>.<br /><span style={{ fontSize: '.78rem' }}>Essayez un autre terme, ou ajoutez la structure via « + Entité ».</span></div>
        )}
      </div>
    ) : null;

    // Arbre
    const posRow = (p, pq) => {
      if (pq && !accHas(pq, p.c, p.role, p.team, p.ao)) return null;
      const st = stageOf(p.stage);
      return (
        <div key={'pos' + p.id} className="tn-row" onClick={() => setModal({ type: 'posDetail', id: p.id })}>
          <span className="tn-tog">·</span>
          <span className="tn-ico p">{initials(p.c)}</span>
          <span className="tn-name" style={{ fontWeight: 600 }}><Hl s={p.c} q={pq} /></span>
          <span className="tn-sub"><Hl s={p.role} q={pq} />{p.team ? <> · <Hl s={p.team} q={pq} /></> : null} · {p.tjm}€/j</span>
          <span className="pill" style={{ background: st.color + '22', color: st.color, marginLeft: 'auto', flexShrink: 0 }}>{st.label}</span>
        </div>
      );
    };
    const subNode = (scl, e, sb, force) => {
      const path = accKey(name, scl.name, e.name, sb.name);
      const ei = items.filter((p) => p.subclient === scl.name && p.entity === e.name && p.sub === sb.name);
      const domaines = sb.domains || [];
      const isOpen = q ? true : accOpen.has(path);
      const eff = force || accHas(q, sb.name, sb.contact, sb.email);
      let kids = null;
      if (q || isOpen) {
        const visD = domaines.filter((d) => !q || eff || accHas(q, d));
        kids = (
          <>
            {visD.length > 0 && (
              <div className="tn-row noh">
                <span className="tn-tog">·</span>
                <span className="tn-sub" style={{ marginRight: 4 }}>Domaines</span>
                <div className="domain-wrap">
                  {visD.map((d) => (
                    <button key={d} className="domain-chip" title={`Envoyer un profil sur ${d}`}
                      onClick={(ev) => { ev.stopPropagation(); posFromTeam(name, scl.name, e.name, sb.name, d); }}>
                      <Hl s={d} q={q} />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {ei.map((p) => posRow(p, eff ? '' : q))}
          </>
        );
      }
      if (q && !eff && !ei.some((p) => accHas(q, p.c, p.role, p.team, p.ao)) && !domaines.some((d) => accHas(q, d))) return null;
      const has = domaines.length || ei.length;
      return (
        <div key={path}>
          <div className="tn-row" onClick={() => accToggle(path)}>
            <span className="tn-tog">{has ? (isOpen || q ? '▾' : '▸') : '·'}</span>
            <span className="tn-ico s">◇</span>
            <span className="tn-name"><Hl s={sb.name} q={q} /></span>
            <span className="tn-sub">{sb.contact ? <><Hl s={sb.contact} q={q} /> · </> : null}{domaines.length} domaine(s){ei.length ? ' · ' + ei.length + ' pos.' : ''}</span>
            <button className="iconbtn mini" title="Envoyer un profil dans cette équipe" onClick={(ev) => { ev.stopPropagation(); posFromTeam(name, scl.name, e.name, sb.name, ''); }}>+ Profil</button>
          </div>
          {(q || isOpen) && <div className="tn-kids">{kids}</div>}
        </div>
      );
    };
    const entityNode = (scl, e, force) => {
      const path = accKey(name, scl.name, e.name);
      const ei = items.filter((p) => p.subclient === scl.name && p.entity === e.name);
      const subs = e.subs || [];
      const direct = ei.filter((p) => !p.sub);
      const isOpen = q ? true : accOpen.has(path);
      const eff = force || accHas(q, e.name);
      const kids = (q || isOpen) ? (
        <>
          {subs.map((s) => subNode(scl, e, s, eff)).filter(Boolean)}
          {direct.map((p) => posRow(p, eff ? '' : q))}
        </>
      ) : null;
      const kidsEmpty = q && !subs.map((s) => subNode(scl, e, s, eff)).filter(Boolean).length && !direct.filter((p) => accHas(q, p.c, p.role, p.team, p.ao)).length;
      if (q && !eff && kidsEmpty) return null;
      const has = subs.length || direct.length;
      return (
        <div key={path}>
          <div className="tn-row" onClick={() => accToggle(path)}>
            <span className="tn-tog">{has ? (isOpen || q ? '▾' : '▸') : '·'}</span>
            <span className="tn-ico e">▢</span>
            <span className="tn-name"><Hl s={e.name} q={q} /></span>
            <span className="tn-sub">{subs.length} équipe(s){ei.length ? ' · ' + ei.length + ' pos.' : ''}</span>
            <button className="iconbtn mini" title="Envoyer un profil dans cette sous-entité" onClick={(ev) => { ev.stopPropagation(); posFromTeam(name, scl.name, e.name, '', ''); }}>+ Profil</button>
          </div>
          {(q || isOpen) && <div className="tn-kids">{kids}</div>}
        </div>
      );
    };
    const clientNode = (scl) => {
      const path = accKey(name, scl.name);
      const ei = items.filter((p) => p.subclient === scl.name);
      const ses = scl.entities || [];
      const direct = ei.filter((p) => !p.entity);
      const isOpen = q ? true : accOpen.has(path);
      const eff = accHas(q, scl.name, scl.contact, scl.email);
      const kidNodes = (q || isOpen) ? ses.map((e) => entityNode(scl, e, eff)).filter(Boolean) : [];
      const directRows = (q || isOpen) ? direct.map((p) => posRow(p, eff ? '' : q)).filter(Boolean) : [];
      if (q && !eff && !kidNodes.length && !directRows.length) return null;
      const has = ses.length || direct.length;
      return (
        <div key={path}>
          <div className="tn-row" onClick={() => accToggle(path)}>
            <span className="tn-tog">{has ? (isOpen || q ? '▾' : '▸') : '·'}</span>
            <span className="tn-ico c">▣</span>
            <span className="tn-name"><Hl s={scl.name} q={q} /></span>
            <span className="tn-sub">{scl.contact ? <Hl s={scl.contact} q={q} /> : 'contact à compléter'} · {ses.length} sous-entité(s){ei.length ? ' · ' + ei.length + ' pos.' : ''}</span>
            <button className="iconbtn ghost mini" title="Modifier / structurer cette entité" onClick={(ev) => { ev.stopPropagation(); setModal({ type: 'subclientForm', gc: name, scl: scl.name }); }}>✎</button>
          </div>
          {(q || isOpen) && <div className="tn-kids">{kidNodes}{directRows}</div>}
        </div>
      );
    };

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <span className="acc-back" onClick={() => navigate('/cockpit/comptes')}>← Retour aux comptes</span>
        </div>

        {/* Bandeau d'identité horizontal */}
        <div className="acc-header">
          <div className="ah-top">
            <div className="acc-id">
              <div className="aav">{initials(name)}</div>
              <div>
                <div className="nm">{name}</div>
                <div className="badge2">Grand compte</div>
                <div className="sub2">{cl.sector || '—'}{cl.city ? ' · ' + cl.city : ''}</div>
              </div>
            </div>
            <div className="ah-actions">
              <button className="btn btn-ghost" onClick={() => setModal({ type: 'clientForm', name })}>Modifier</button>
              <button className="btn btn-ghost" onClick={() => setModal({ type: 'subclientForm', gc: name, scl: null })}>+ Entité</button>
              <button className="btn btn-primary" onClick={() => setModal({ type: 'posForm', preset: { client: name } })}>+ Positionnement</button>
            </div>
          </div>
          <div className="ah-strip">
            <div className="ah-kpis">
              <div className="acc-stat"><div className="sk">Pipeline pondéré</div><div className="sv">{open.length ? fmt(Math.round(pond)) : dash}</div></div>
              <div className="acc-stat"><div className="sk">CA gagné</div><div className="sv" style={{ color: won.length ? 'var(--win)' : undefined }}>{won.length ? fmt(ca) : dash}</div></div>
              <div className="acc-stat"><div className="sk">Taux transfo</div><div className="sv">{(won.length + lost) ? tx + ' %' : dash}</div></div>
              <div className="acc-stat"><div className="sk">AOs en cours</div><div className="sv">{new Set(open.map((p) => p.ao)).size || dash}</div></div>
            </div>
            <div className="ah-fields">
              <div className="ah-field"><div className="k">Contact clé</div><div className="v">{cl.contact || '—'}</div></div>
              <div className="ah-field"><div className="k">Email</div><div className="v">{cl.email ? <a href={`mailto:${cl.email}`}>{cl.email}</a> : '—'}</div></div>
              <div className="ah-field"><div className="k">Téléphone</div><div className="v">{cl.phone || '—'}</div></div>
              <div className="ah-field"><div className="k">Entités · pos.</div><div className="v">{scls.length} · {items.length}</div></div>
            </div>
          </div>
        </div>

        {/* Recherche instantanée */}
        <div className="acc-cmd">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <input ref={accSearchRef} value={accQ} onChange={(e) => setAccQ(e.target.value)} autoComplete="off"
            placeholder="Rechercher une équipe, un domaine, un contact…  (ex. « cash management », raccourci « / »)" />
          {accQ && <button className="clr" onClick={() => { setAccQ(''); accSearchRef.current?.focus(); }}>Effacer ✕</button>}
        </div>
        {results}

        {/* Structure du compte */}
        <div className="acc-sec-head">
          <div className="t">Structure du compte</div>
          <div className="c">{scls.length} entité(s) · {items.length} positionnement(s) · cliquez un nœud pour déplier</div>
        </div>
        <div className="tree-toolbar">
          <button className="btn btn-ghost" onClick={() => setAccOpen(new Set(accAllPaths()))}>Tout déplier</button>
          <button className="btn btn-ghost" onClick={() => setAccOpen(new Set())}>Tout replier</button>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'subclientForm', gc: name, scl: null })}>+ Entité</button>
        </div>
        <div className="acc-tree">
          <div className="tn-row noh">
            <span className="tn-tog">·</span>
            <span className="tn-ico gc">{initials(name)}</span>
            <span className="tn-name">{name}</span>
            <span className="tn-sub">{scls.length} entité(s) · {items.length} pos.</span>
          </div>
          <div className="tn-kids" style={{ borderLeft: 'none', paddingLeft: 4, marginLeft: 6 }}>
            {scls.map(clientNode).filter(Boolean).length
              ? scls.map(clientNode)
              : <div className="ms" style={{ padding: '8px 0 0 4px' }}>Aucune entité{q ? ` ne correspond à « ${q} »` : ' — cliquez « + Entité » pour cartographier'}.</div>}
          </div>
        </div>

        {/* Positionnements du compte */}
        <div className="acc-sec-head" style={{ marginTop: 26 }}>
          <div className="t">Positionnements</div>
          <div className="c">{items.length} au total</div>
        </div>
        {items.length ? items.map((p) => {
          const st = stageOf(p.stage);
          return (
            <div key={p.id} className="pos-line" onClick={() => setModal({ type: 'posDetail', id: p.id })}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{p.c}</div>
                <div className="ms">{p.role} · {[p.subclient, p.sub].filter(Boolean).join(' › ') || p.ao}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className="pill" style={{ background: st.color + '22', color: st.color }}>{st.label}</span>
                <div className="ms" style={{ marginTop: 3 }}>{p.tjm}€/j</div>
              </div>
            </div>
          );
        }) : <div className="ms">Aucun positionnement sur ce compte.</div>}
      </div>
    );
  };

  const renderConsultants = () => {
    const anticip = cons.filter((co) => (co.statut === 'En mission' || co.statut === 'Bientôt dispo') && daysLeft(co.end) != null && daysLeft(co.end) <= INTERCO_DAYS);
    const interco = cons.filter((c) => c.statut === 'Intercontrat');
    if (!cons.length) {
      return (
        <div className="ckpt-empty">
          <div className="ic">👤</div>
          <div className="t">Aucun consultant dans le vivier</div>
          <div className="d">Ajoutez vos consultants avec leurs compétences, TJM et disponibilité pour pouvoir les matcher et les positionner.</div>
          <button className="btn btn-primary" onClick={() => setModal({ type: 'consForm' })}>+ Ajouter un consultant</button>
        </div>
      );
    }
    return (
      <div>
        {(anticip.length > 0 || interco.length > 0) && (
          <div className="banner">
            ⚠ À anticiper : {interco.length} consultant(s) en intercontrat{anticip.length ? ` et ${anticip.length} fin(s) de mission sous 30 jours` : ''}. Lancez un matching pour les replacer.
          </div>
        )}
        <div className="cardsgrid">
          {filteredCons.slice().sort((a, b) => (isInterco(b) - isInterco(a)) || a.name.localeCompare(b.name)).map((co) => {
            const col = STATUTS[co.statut] || '#6f6e8c';
            const dl = daysLeft(co.end);
            const flag = isInterco(co);
            let badge = co.statut;
            if ((co.statut === 'En mission' || co.statut === 'Bientôt dispo') && dl != null && dl <= INTERCO_DAYS) badge = 'Fin dans ' + dl + ' j';
            return (
              <div key={co.id} className={`client-card${flag ? ' flag' : ''}`} onClick={() => setModal({ type: 'consDetail', id: co.id })}>
                <div className="ch">
                  <div className="clogo">{initials(co.name)}</div>
                  <div><div className="cn">{co.name}</div><div className="cmeta">{co.role} · {co.tjm}€/j</div></div>
                </div>
                <div className="statline"><span className="sdot" style={{ background: col }} /><span style={{ color: flag ? 'var(--lose)' : 'var(--text)' }}>{badge}</span></div>
                <div className="skills">{co.skills.map((s) => <span key={s} className="skill">{s}</span>)}</div>
                <div className="cfoot"><span>{co.client ? 'Mission : ' + co.client : 'Sans mission'}</span>{flag ? <span className="hot">à replacer</span> : <span>OK</span>}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMatching = () => {
    const avail = cons.filter(matchable);
    let pairs = [];
    needs.forEach((n) => avail.forEach((co) => { const sc = matchScore(co, n); if (sc >= 40) pairs.push({ co, n, sc }); }));
    if (filters.focus === 'interco') pairs = pairs.filter((p) => isInterco(p.co));
    pairs.sort((a, b) => (isInterco(b.co) - isInterco(a.co)) || b.sc - a.sc);
    pairs = pairs.slice(0, 12);
    return (
      <div className="cardsgrid">
        {pairs.length ? pairs.map(({ co, n, sc }, i) => (
          <div key={i} className={`client-card${isInterco(co) ? ' flag' : ''}`} style={{ cursor: 'default' }}>
            <div className="ch" style={{ marginBottom: 10 }}>
              <div className="clogo">{initials(co.name)}</div>
              <div><div className="cn" style={{ fontSize: '.95rem' }}>{co.name}</div><div className="cmeta">{co.role}{isInterco(co) ? ' · à replacer' : ' · ' + co.statut}</div></div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: -1, color: sc >= 70 ? 'var(--win)' : 'var(--acc)' }}>{sc}%</div>
                <div className="cmeta">fit</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.8rem', fontWeight: 600, marginBottom: 10 }}>→ {n.title} · {n.client}{n.subclient ? ' / ' + n.subclient : ''}</div>
            <div className="skills">{n.skills.map((s) => <span key={s} className={`skill${co.skills.includes(s) ? '' : ' off'}`}>{s}</span>)}</div>
            <div className="cfoot">
              <span>{n.tjm}€/j cible</span>
              <button className="btn btn-primary" style={{ padding: '6px 13px' }} onClick={() => posFromMatch(co.id, n.id)}>Positionner</button>
            </div>
          </div>
        )) : <div style={{ color: 'var(--muted2)', padding: 20 }}>Aucun rapprochement ≥ 40%. Ajoutez des consultants disponibles ou des besoins.</div>}
      </div>
    );
  };

  const renderAOs = () => (
    <div>
      <div style={{ marginBottom: 14 }}><span className="acc-back" onClick={() => navigate('/cockpit')}>← Tableau de bord</span></div>
      <div className="cardsgrid">
      {needs.length ? needs.slice().sort((a, b) => ((daysLeft(a.deadline) ?? 999) - (daysLeft(b.deadline) ?? 999))).map((n) => {
        const dl = daysLeft(n.deadline);
        const cand = candidatesFor(pos, n).length;
        const best = bestMatchFor(cons, n);
        const urgent = dl != null && dl <= 7;
        return (
          <div key={n.id} className={`client-card${urgent ? ' flag' : ''}`} onClick={() => setModal({ type: 'needDetail', id: n.id })}>
            <div className="ch" style={{ marginBottom: 10 }}>
              <div className="clogo">📋</div>
              <div><div className="cn" style={{ fontSize: '.95rem' }}>{n.title}</div><div className="cmeta">{n.client}{n.subclient ? ' / ' + n.subclient : ''}</div></div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: '.85rem', fontWeight: 800, color: urgent ? 'var(--lose)' : 'var(--text)' }}>{dl != null ? (dl < 0 ? 'échue' : 'J-' + dl) : '—'}</div>
                <div className="cmeta">{n.deadline || ''}</div>
              </div>
            </div>
            <div className="skills">{n.skills.map((s) => <span key={s} className="skill">{s}</span>)}</div>
            <div className="cfoot">
              <span>{n.tjm}€/j · {n.source || ''}</span>
              <span>{cand ? cand + ' candidat(s)' : <span style={{ color: 'var(--a)', fontWeight: 700 }}>à staffer</span>} · best {best}%</span>
            </div>
          </div>
        );
      }) : <div style={{ color: 'var(--muted2)', padding: 20 }}>Aucun AO. Cliquez « + Nouvel AO ».</div>}
      </div>
    </div>
  );

  const renderShortlists = () => (
    <div>
      <div style={{ marginBottom: 14 }}><span className="acc-back" onClick={() => navigate('/cockpit')}>← Tableau de bord</span></div>
      <div className="cardsgrid">
      {shortlists.length ? shortlists.map((sl) => {
        const col = SLSTATUS[sl.status] || '#6f6e8c';
        const ret = sl.cands.filter((c) => c.state === 'retenu').length;
        const best = sl.cands.reduce((m, c) => { const co = cons.find((x) => x.id === c.id); return co ? Math.max(m, matchScore(co, { skills: sl.skills })) : m; }, 0);
        return (
          <div key={sl.id} className="client-card" onClick={() => setModal({ type: 'slDetail', id: sl.id })}>
            <div className="ch" style={{ marginBottom: 10 }}>
              <div className="clogo">≡</div>
              <div><div className="cn" style={{ fontSize: '.95rem' }}>{sl.title}</div><div className="cmeta">{sl.client}{sl.subclient ? ' / ' + sl.subclient : ''}</div></div>
              <div style={{ marginLeft: 'auto' }}><span className="pill" style={{ background: col + '22', color: col }}>{sl.status}</span></div>
            </div>
            <div className="cgrid">
              <div className="stat"><div className="sk">Candidats</div><div className="sv">{sl.cands.length}</div></div>
              <div className="stat"><div className="sk">Retenus</div><div className="sv win">{ret}</div></div>
              <div className="stat"><div className="sk">Meilleur fit</div><div className="sv">{best}%</div></div>
              <div className="stat"><div className="sk">Commercial</div><div className="sv" style={{ fontSize: '.9rem' }}>{sl.owner}</div></div>
            </div>
            <div className="cfoot"><span>{sl.created}</span><span>{sl.ao}</span></div>
          </div>
        );
      }) : <div style={{ color: 'var(--muted2)', padding: 20 }}>Aucune shortlist. Cliquez « + Nouvelle shortlist ».</div>}
      </div>
    </div>
  );

  // ── Modales de détail (stateless, lisent db en direct) ─────────────────────
  const renderModal = () => {
    if (!modal) return null;
    if (modal.type === 'posForm') return <PosFormModal db={db} id={modal.id} preset={modal.preset} onClose={() => setModal(null)} commit={update} onDraft={draftToClient} />;
    if (modal.type === 'needForm') return <NeedFormModal db={db} id={modal.id} onClose={() => setModal(null)} onSave={saveNeed} />;
    if (modal.type === 'slForm') {
      if (!needs.length) { notify("Créez d'abord un appel d'offres."); setModal(null); return null; }
      return <SlFormModal db={db} onClose={() => setModal(null)} onCreate={createShortlist} />;
    }
    if (modal.type === 'clientForm') return <ClientFormModal db={db} name={modal.name} onClose={() => setModal(null)} onSave={saveClient} onDelete={delClient} />;
    if (modal.type === 'subclientForm') return <SubclientFormModal db={db} gc={modal.gc} sclName={modal.scl} onClose={() => setModal(null)} onSave={saveSubclient} onDelete={delSubclient} />;
    if (modal.type === 'consForm') return <ConsFormModal db={db} id={modal.id} onClose={() => setModal(null)} onSave={saveCons} />;

    if (modal.type === 'posDetail') {
      const p = pos.find((x) => x.id === modal.id);
      if (!p) return null;
      return (
        <ModalShell onClose={() => setModal(null)}>
          <ModalHead av={initials(p.c)} title={p.c} sub={p.role} onClose={() => setModal(null)} />
          <div className="modal-body">
            <div className="field-grid">
              <div className="field"><div className="k">Compte</div><div className="v">{p.client}</div></div>
              <div className="field"><div className="k">Entité</div><div className="v">{p.subclient || '—'}</div></div>
              <div className="field"><div className="k">Sous-entité</div><div className="v">{p.entity || '—'}</div></div>
              <div className="field"><div className="k">Équipe</div><div className="v">{p.sub || '—'}</div></div>
              <div className="field"><div className="k">Domaine</div><div className="v">{p.team || '—'}</div></div>
              <div className="field"><div className="k">Appel d'offres</div><div className="v">{p.ao}</div></div>
              <div className="field"><div className="k">TJM proposé</div><div className="v">{p.tjm} €/jour</div></div>
              <div className="field"><div className="k">Score matching</div><div className="v" style={{ color: 'var(--acc)' }}>{['gagne', 'perdu'].includes(p.stage) ? '—' : p.match + ' %'}</div></div>
              <div className="field"><div className="k">Commercial</div><div className="v">{p.owner}</div></div>
              <div className="field"><div className="k">Source / canal</div><div className="v">{p.source}</div></div>
              <div className="field"><div className="k">Valeur mission est.</div><div className="v">{fmt(missionVal(p))}</div></div>
            </div>
            <div className="field"><div className="k" style={{ marginBottom: 4 }}>Étape du closing</div></div>
            <div className="stage-row">
              {STAGES.map((st) => (
                <button key={st.id} className={`sg${st.id === p.stage ? ' cur' : ''}`} onClick={() => moveStage(p.id, st.id)}>{st.label}</button>
              ))}
            </div>
            {!['gagne', 'perdu'].includes(p.stage) && (
              <div className="links">
                <button className="link-btn" style={{ color: 'var(--acc)', borderColor: 'var(--violet)' }} onClick={() => relancer(p.id)}>Relancer</button>
                <button className="link-btn" onClick={() => draftToClient({ client: p.client, subclient: p.subclient, entity: p.entity, sub: p.sub, team: p.team, cons: p.c, role: p.role, ao: p.ao, tjm: p.tjm })}>Brouillon mail</button>
              </div>
            )}
            <div className="timeline">
              <div className="tl-t">Historique</div>
              {p.hist.map((h, i) => (
                <div key={i} className="ev"><div className="d" /><div><div>{h[0]}</div><div className="when">{h[1]}</div></div></div>
              ))}
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => delPos(p.id)}>Supprimer</button>
            <button className="btn btn-ghost" onClick={() => setModal({ type: 'posForm', id: p.id })}>Modifier</button>
            <button className="btn btn-primary" onClick={() => setModal(null)}>Fermer</button>
          </div>
        </ModalShell>
      );
    }

    if (modal.type === 'needDetail') {
      const n = needs.find((x) => x.id === modal.id);
      if (!n) return null;
      const dl = daysLeft(n.deadline);
      const cands = cons.filter(matchable).map((co) => ({ co, sc: matchScore(co, n) })).filter((x) => x.sc > 0).sort((a, b) => b.sc - a.sc).slice(0, 5);
      return (
        <ModalShell onClose={() => setModal(null)}>
          <ModalHead av="AO" title={n.title} sub={`${n.client}${n.subclient ? ' / ' + n.subclient : ''}`} onClose={() => setModal(null)} />
          <div className="modal-body">
            <div className="field-grid">
              <div className="field"><div className="k">TJM cible</div><div className="v">{n.tjm} €/jour</div></div>
              <div className="field"><div className="k">Échéance</div><div className="v" style={dl != null && dl <= 7 ? { color: 'var(--lose,#F87171)' } : undefined}>{n.deadline || '—'}{dl != null ? ` (J-${dl})` : ''}</div></div>
              <div className="field"><div className="k">Source</div><div className="v">{n.source || '—'}</div></div>
              <div className="field"><div className="k">Candidats positionnés</div><div className="v">{candidatesFor(pos, n).length}</div></div>
            </div>
            <div className="field"><div className="k" style={{ marginBottom: 6 }}>Compétences requises</div></div>
            <div className="skills" style={{ marginBottom: 18 }}>{n.skills.map((s) => <span key={s} className="skill">{s}</span>)}</div>
            <div className="mini-list">
              <div className="tl-t">Meilleurs candidats disponibles</div>
              {cands.length ? cands.map(({ co, sc }) => (
                <div key={co.id} className="mini-row" style={{ cursor: 'default' }}>
                  <div><div className="ml">{co.name}{isInterco(co) ? ' · à replacer' : ''}</div><div className="ms">{co.role} · {co.skills.filter((s) => n.skills.includes(s)).join(', ')}</div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="pill" style={{ background: sc >= 70 ? 'rgba(74,222,128,.16)' : 'var(--violet-soft,#1d1840)', color: sc >= 70 ? 'var(--win)' : 'var(--acc)' }}>{sc}%</span>
                    <button className="btn btn-primary" style={{ padding: '5px 11px' }} onClick={() => posFromMatch(co.id, n.id)}>Positionner</button>
                  </div>
                </div>
              )) : <div className="ms">Aucun consultant disponible ne correspond. Élargissez le vivier.</div>}
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => delNeed(n.id)}>Supprimer</button>
            <button className="btn btn-ghost" onClick={() => setModal({ type: 'needForm', id: n.id })}>Modifier</button>
            <button className="btn btn-primary" onClick={() => { setModal(null); navigate('/cockpit/matching'); }}>Lancer le matching</button>
          </div>
        </ModalShell>
      );
    }

    if (modal.type === 'slDetail') {
      const sl = shortlists.find((x) => x.id === modal.id);
      if (!sl) return null;
      const col = SLSTATUS[sl.status] || '#6f6e8c';
      const inSl = sl.cands.map((c) => c.id);
      const avail = cons.filter((c) => !inSl.includes(c.id));
      return (
        <ModalShell onClose={() => setModal(null)}>
          <ModalHead av="≡" title={sl.title} sub={`${sl.client} · ${sl.owner}`} onClose={() => setModal(null)} />
          <div className="modal-body">
            <div style={{ marginBottom: 16 }}><span className="pill" style={{ background: col + '22', color: col }}>{sl.status}</span></div>
            <div className="field"><div className="k" style={{ marginBottom: 6 }}>Compétences du besoin</div></div>
            <div className="skills" style={{ marginBottom: 18 }}>{sl.skills.map((s) => <span key={s} className="skill">{s}</span>)}</div>
            <div className="mini-list">
              <div className="tl-t">Candidats ({sl.cands.length}) — cliquez le statut pour basculer proposé / retenu / écarté</div>
              {sl.cands.length ? sl.cands.map((c) => {
                const co = cons.find((x) => x.id === c.id);
                if (!co) return null;
                const sc = matchScore(co, { skills: sl.skills });
                const cc = CANDST[c.state];
                return (
                  <div key={c.id} className="mini-row" style={{ cursor: 'default' }}>
                    <div><div className="ml">{co.name}</div><div className="ms">{co.role} · {co.tjm}€/j</div></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="pill" style={{ background: 'var(--violet-soft,#1d1840)', color: 'var(--acc)' }}>{sc}%</span>
                      <button className="pill" style={{ background: cc + '22', color: cc, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => cycleCand(sl.id, co.id)}>{c.state}</button>
                      <button style={{ cursor: 'pointer', color: '#6b6a8a', background: 'none', border: 'none', fontSize: '.9rem' }} onClick={() => rmCand(sl.id, co.id)} aria-label="Retirer">✕</button>
                    </div>
                  </div>
                );
              }) : <div className="ms">Aucun candidat. Ajoutez-en ci-dessous.</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <select id="ckpt-sl-add" style={{ flex: 1 }} defaultValue="">
                  <option value="">+ Ajouter un consultant…</option>
                  {avail.map((co) => <option key={co.id} value={String(co.id)}>{co.name} — {co.role}</option>)}
                </select>
                <button className="btn btn-ghost" onClick={() => {
                  const el = document.getElementById('ckpt-sl-add');
                  if (el && el.value) { addCand(sl.id, +el.value); el.value = ''; }
                }}>Ajouter</button>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => delSl(sl.id)}>Supprimer</button>
            <button className="btn btn-primary" onClick={() => sendShortlist(sl.id)}>Envoyer au client →</button>
          </div>
        </ModalShell>
      );
    }

    if (modal.type === 'consDetail') {
      const co = cons.find((x) => x.id === modal.id);
      if (!co) return null;
      const dl = daysLeft(co.end);
      const flag = isInterco(co);
      return (
        <ModalShell onClose={() => setModal(null)}>
          <ModalHead av={initials(co.name)} title={co.name} sub={co.role} onClose={() => setModal(null)} />
          <div className="modal-body">
            {flag && (
              <div className="banner">
                ⚠ {co.statut === 'Intercontrat' ? 'En intercontrat — à replacer en priorité.' : `Fin de mission dans ${dl} jours — anticipez le replacement.`}
              </div>
            )}
            <div className="field-grid">
              <div className="field"><div className="k">Statut</div><div className="v" style={{ color: STATUTS[co.statut] }}>{co.statut}</div></div>
              <div className="field"><div className="k">TJM</div><div className="v">{co.tjm} €/jour</div></div>
              <div className="field"><div className="k">Mission actuelle</div><div className="v">{co.client || '—'}</div></div>
              <div className="field"><div className="k">Fin de mission</div><div className="v">{co.end ? co.end + (dl != null ? ` (${dl} j)` : '') : '—'}</div></div>
            </div>
            <div className="field"><div className="k" style={{ marginBottom: 6 }}>Compétences</div></div>
            <div className="skills" style={{ marginBottom: 18 }}>{co.skills.map((s) => <span key={s} className="skill">{s}</span>)}</div>
            <div className="links">
              <button className="link-btn" style={{ color: 'var(--acc)', borderColor: 'var(--violet)' }} onClick={() => { setModal(null); posFromCons(co.id); }}>Positionner sur un AO</button>
            </div>
            <div className="timeline">
              <div className="tl-t">Historique</div>
              {co.hist.map((h, i) => (
                <div key={i} className="ev"><div className="d" /><div><div>{h[0]}</div><div className="when">{h[1]}</div></div></div>
              ))}
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={() => delCons(co.id)}>Supprimer</button>
            <button className="btn btn-ghost" onClick={() => setModal({ type: 'consForm', id: co.id })}>Modifier</button>
            <button className="btn btn-primary" onClick={() => setModal(null)}>Fermer</button>
          </div>
        </ModalShell>
      );
    }
    return null;
  };

  // ── Barre de navigation interne + actions ───────────────────────────────────
  const topAction = () => {
    if (view === 'ao') return <button className="btn btn-primary" onClick={() => setModal({ type: 'needForm' })}>+ Nouvel AO</button>;
    if (view === 'shortlists') return <button className="btn btn-primary" onClick={() => setModal({ type: 'slForm' })}>+ Nouvelle shortlist</button>;
    if (view === 'consultants') return <button className="btn btn-primary" onClick={() => setModal({ type: 'consForm' })}>+ Consultant</button>;
    if (view === 'account') return null;
    return (
      <>
        <button className="btn btn-ghost" onClick={() => setModal({ type: 'clientForm', name: null })}>+ Grand compte</button>
        <button className="btn btn-primary" onClick={() => setModal({ type: 'posForm' })}>+ Positionnement</button>
      </>
    );
  };

  const body = view === 'cockpit' ? renderCockpitView()
    : view === 'kanban' ? renderKanban()
    : view === 'list' ? renderList()
    : view === 'clients' ? renderClients()
    : view === 'account' ? renderAccount()
    : view === 'consultants' ? renderConsultants()
    : view === 'matching' ? renderMatching()
    : view === 'ao' ? renderAOs()
    : renderShortlists();

  return (
    <AppLayout title={`Cockpit · ${VIEW_LABEL[view] || 'Tableau de bord'}`}>
      <style>{COCKPIT_CSS}</style>
      <div className="ckpt">
        {/* Barre d'actions contextuelles (la navigation vit dans la sidebar) */}
        <div className="ckpt-actions">
          <button className="icon-btn" title="Tutoriel de cette page" aria-label="Tutoriel de cette page"
            onClick={() => { if (tutoVisible) closeTuto(); else setTutoForce(true); }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="12" r="9" /><path d="M9.2 9a2.8 2.8 0 0 1 5.6.4c0 1.8-2.8 2.2-2.8 3.6" /><path d="M12 17h.01" /></svg>
          </button>
          {topAction()}
        </div>

        {/* Tutoriel contextuel */}
        {tutoVisible && (
          <div className="tuto" role="note">
            <button className="x2" onClick={closeTuto} title="Fermer" aria-label="Fermer le tutoriel"><XIcon /></button>
            <div className="tuto-t">
              <span className="ico">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 1 3.6 10.8c-.7.6-1.1 1.3-1.3 2.2h-4.6c-.2-.9-.6-1.6-1.3-2.2A6 6 0 0 1 12 3z" /></svg>
              </span>
              {tuto.title}
            </div>
            <div className="tuto-steps">
              {tuto.steps.map((s, i) => (
                <div key={i} className="tuto-step"><span className="n">{i + 1}</span><div><b>{s[0]}</b><span>{s[1]}</span></div></div>
              ))}
            </div>
            <div className="tuto-foot">
              <button className="btn btn-primary" style={{ padding: '7px 15px' }} onClick={closeTuto}>Compris ✓</button>
              <span className="hint">Revoyez ce guide à tout moment avec le bouton « ? » ci-dessus.</span>
            </div>
          </div>
        )}

        {renderKpis()}

        {/* Filtres contextuels */}
        {(view === 'kanban' || view === 'list') && (
          <div className="toolbar">
            <div className="seg">
              <button className={view === 'kanban' ? 'on' : ''} onClick={() => setView('kanban')}>Kanban</button>
              <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>Liste</button>
            </div>
            {posFilters}
          </div>
        )}
        {view === 'clients' && (
          <div className="toolbar">
            {posFilters}
            <input style={{ flex: 1, minWidth: 220 }} value={globalQ} onChange={(e) => setGlobalQ(e.target.value)}
              placeholder="Rechercher un domaine ou une compétence… (ex. cash management)" />
          </div>
        )}
        {view === 'consultants' && (
          <div className="toolbar">
            <select value={filters.statut} onChange={(e) => setFilters((f) => ({ ...f, statut: e.target.value }))}>
              <option value="">Tous les statuts</option>
              {Object.keys(STATUTS).map((s) => <option key={s}>{s}</option>)}
            </select>
            <select value={filters.skill} onChange={(e) => setFilters((f) => ({ ...f, skill: e.target.value }))}>
              <option value="">Toutes les compétences</option>
              {[...new Set(cons.flatMap((c) => c.skills))].sort().map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        )}
        {view === 'matching' && (
          <div className="toolbar">
            <select value={filters.focus} onChange={(e) => setFilters((f) => ({ ...f, focus: e.target.value }))}>
              <option value="">Tous les rapprochements</option>
              <option value="interco">Intercontrats à replacer</option>
            </select>
          </div>
        )}

        {body}
        {renderModal()}
      </div>
    </AppLayout>
  );
}
